import {
  ExchangeAntimalwareSearchableKeywords,
  ExchangeAntispamKeywords,
  ExchangeContentControlSearchableKeywords,
  ExchangeGeneralKeywords,
  ExchangeUserGroupStatus,
} from './exchange-protection.types';
import { SearchableKeyword } from '../../../../../../ui/gzSearchableAccordionsPanel/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import {
  ExchangeAntimalwareRuleActionType,
  ExchangeRuleScopeApplyToType,
  ExchangeRuleScopeEmailSelection,
  ExchangeAntimalwareRuleSettingsScanMode,
  ExchangeAntimalwareScanTaskTargetExclusionType,
  ExchangeAntimalwareScanTaskTargetType,
  ExchangeAntimalwareSection,
  ExchangeAntimalwareSettings,
  ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType,
  ExchangeContentControlRuleActionType,
  ExchangeContentControlSection,
  ExchangeAntispamRuleActionType,
  ExchangeAntispamActiveState,
  ExchangeAntispamAggressivity,
} from '@nirvana/shared/dtos/types/policies/common';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import { ColumnValue } from '../../../../../../grid/types/common';
import { ListItem } from '../../../../../../ui/gzSingleSelectList/types';

export const MAX_CHARS_SUPPORTED = 524288;
export const ON_DEMAND_USERNAME_MAX_LENGTH = 260;

export const MIN_RECURRENCE_STEP = 1;
export const MAX_RECURRENCE_STEP = 999;
export const DEFAULT_RECURRENCE_STEP = 1;
export const DEFAULT_SCAN_ONLY_RECURRENCE_STEP = 3;

export const MIN_SIZE = 1;
export const MAX_SIZE = 999;
export const DEFAULT_SIZE = 3;
export const DEFAULT_DEPTH = 4;

export const EXHANGE_ANTIMALWARE_SETTINGS_DEFAULT_VALUE: ExchangeAntimalwareSettings = {
  appendFooter: true,
  footerText: 'This email was scanned by Bitdefender',
  infectedText: 'The $FILENAME attachment was infected with $VIRUS. The attachment was deleted by Bitdefender.',
  quarantinedText:
    'The $FILENAME attachment was infected with $VIRUS. The attachment was moved to quarantine by Bitdefender.',
  unscannableInfectedText: 'The $FILENAME attachment could not be scanned. The attachment was deleted by Bitdefender.',
  unscannableQuarantinedText:
    'The $FILENAME attachment could not be scanned. The attachment was moved to quarantine by Bitdefender.',
};
export const EXHANGE_ANTIMALWARE_DEFAULT_VALUE: ExchangeAntimalwareSection = {
  enable: true,
  settings: EXHANGE_ANTIMALWARE_SETTINGS_DEFAULT_VALUE,
  rules: [],
  exclusions: [],
  scanTasks: [],
};
export const EXCHANGE_CONTENT_CONTROL_DEFAULT_VALUE: ExchangeContentControlSection = {
  attachmentFiltering: {
    enable: false,
    settings: {
      replacementText:
        'A Bitdefender attachment filtering rule detected the $FILENAME attachment and deleted it. Contact your email administrator for more information.',
    },
    exclusions: {
      trustedSenders: [],
      trustedRecipients: [],
      excludeOnlyIfAllRecipientsAreTrusted: false,
    },
    rules: [],
  },
  contentFiltering: {
    enable: false,
    exclusions: {
      trustedSenders: [],
      trustedRecipients: [],
      excludeOnlyIfAllRecipientsAreTrusted: false,
    },
    rules: [],
  },
};

export function getExchangeProtectionGeneralSearchableKeywords(): {
  [type in ExchangeGeneralKeywords]: SearchableKeyword;
} {
  return {
    [ExchangeGeneralKeywords.General]: {
      translatedLabel: translateFn('General'),
      label: 'General',
    },
    [ExchangeGeneralKeywords.UserGroups]: {
      translatedLabel: translateFn('User groups'),
      label: 'User Groups',
    },
    [ExchangeGeneralKeywords.DomainIpCheck]: {
      translatedLabel: translateFn('Domain IP Check (Antispoofing)'),
      label: 'Domain IP Check',
    },
    [ExchangeGeneralKeywords.DeleteQuarantinedFiles]: {
      translatedLabel: translateFn('Delete quarantined files older than (days)'),
      label: 'Delete quarantined files older than (days)',
    },
    [ExchangeGeneralKeywords.ConnectionBlacklist]: {
      translatedLabel: translateFn('Connection blacklist'),
      label: 'Connection Blacklist',
    },
  };
}
export function getExchangeAntispamSearchableKeywords(): {
  [type in ExchangeAntispamKeywords]: SearchableKeyword;
} {
  return {
    [ExchangeAntispamKeywords.Antispam]: {
      translatedLabel: translateFn('Antispam'),
      label: 'Antispam',
    },
    [ExchangeAntispamKeywords.AntispamFiltering]: {
      translatedLabel: translateFn('Antispam filtering'),
      label: 'Antispam Filtering',
    },
    [ExchangeAntispamKeywords.Settings]: {
      translatedLabel: translateFn('Settings'),
      label: 'Settings',
    },
    [ExchangeAntispamKeywords.RblSettings]: {
      translatedLabel: translateFn('Real-time Blackhole List (RBL) Settings'),
      label: 'Real-time Blackhole List (RBL) Settings',
    },
    [ExchangeAntispamKeywords.DnsQueryTimeout]: {
      translatedLabel: translateFn('DNS query timeout (miliseconds)'),
      label: 'DNS query timeout (miliseconds)',
    },
    [ExchangeAntispamKeywords.DnsIpAddress]: {
      translatedLabel: translateFn('DNS IP address'),
      label: 'DNS IP address',
    },
    [ExchangeAntispamKeywords.WhiteList]: {
      translatedLabel: translateFn('Whitelist'),
      label: 'Whitelist',
    },
  };
}
export function getGroupTypeValues(): Array<ColumnValue> {
  return [
    {
      label: translateFn('Custom'),
      value: 1,
    },
    {
      label: translateFn('Active Directory'),
      value: 2,
    },
  ];
}
export function getExchangeAntimalwareSearchableKeywords(): {
  [type in ExchangeAntimalwareSearchableKeywords]: SearchableKeyword;
} {
  return {
    [ExchangeAntimalwareSearchableKeywords.Antimalware]: {
      translatedLabel: translateFn('Antimalware'),
      label: 'Antimalware',
    },
    [ExchangeAntimalwareSearchableKeywords.AntimalwareFiltering]: {
      translatedLabel: translateFn('Antimalware Filtering'),
      label: 'Antimalware Filtering',
    },
    [ExchangeAntimalwareSearchableKeywords.EditNotes]: {
      translatedLabel: translateFn('Edit notes'),
      label: 'Edit notes',
    },
    [ExchangeAntimalwareSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [ExchangeAntimalwareSearchableKeywords.ScanTasks]: {
      translatedLabel: translateFn('Scan tasks'),
      label: 'Scan tasks',
    },
    [ExchangeAntimalwareSearchableKeywords.OnDemandScanCredentials]: {
      translatedLabel: translateFn('On-demand scan credentials'),
      label: 'On-demand scan credentials',
    },
  };
}

export function getExchangeContentControlSearchableKeywords(): {
  [type in ExchangeContentControlSearchableKeywords]: SearchableKeyword;
} {
  return {
    [ExchangeContentControlSearchableKeywords.ContentControl]: {
      translatedLabel: translateFn('Content Control'),
      label: 'Content Control',
    },
    [ExchangeContentControlSearchableKeywords.ContentFiltering]: {
      translatedLabel: translateFn('Content Filtering'),
      label: 'Content Filtering',
    },
    [ExchangeContentControlSearchableKeywords.Exclusions]: {
      translatedLabel: translateFn('Exclusions'),
      label: 'Exclusions',
    },
    [ExchangeContentControlSearchableKeywords.TrustedSenders]: {
      translatedLabel: translateFn('Trusted senders'),
      label: 'Trusted senders',
    },
    [ExchangeContentControlSearchableKeywords.TrustedRecipients]: {
      translatedLabel: translateFn('Trusted recipients'),
      label: 'Trusted recipients',
    },
    [ExchangeContentControlSearchableKeywords.ExcludeEmail]: {
      translatedLabel: translateFn('Exclude email from filtering only if all recipients are trusted'),
      label: 'Exclude email from filtering only if all recipients are trusted',
    },
    [ExchangeContentControlSearchableKeywords.AttachmentFiltering]: {
      translatedLabel: translateFn('Attachment Filtering'),
      label: 'Attachment Filtering',
    },
    [ExchangeContentControlSearchableKeywords.Settings]: {
      translatedLabel: translateFn('Settings'),
      label: 'Settings',
    },
    [ExchangeContentControlSearchableKeywords.ReplacementText]: {
      translatedLabel: translateFn('Replacement text for detected attachments'),
      label: 'Replacement text for detected attachments',
    },
  };
}

export function getExchangeProtectionGeneralDefaultValue() {
  return {
    blackList: {
      enable: false,
      list: [],
    },
    quarantine: {
      deleteFilesOlderThan: Application.Main.product.isCloud() ? 15 : 30,
    },
    antispoofing: {
      enable: false,
      domainIpList: [],
    },
  };
}
export function getExchangeAntispamDefaultValue() {
  return {
    enable: true,
    settings: {
      rblDnsAddress: '',
      rblDnsMsTimeout: 500,
      rblServers: [],
    },
    whiteList: {
      enable: false,
      list: [],
    },
    rules: [],
  };
}

export function getExchangeApplyToRule(value: ExchangeRuleScopeApplyToType): string {
  switch (value) {
    case ExchangeRuleScopeApplyToType.INCOMING:
      return translateFn('Incoming emails');
    case ExchangeRuleScopeApplyToType.INTERNAL:
      return translateFn('Internal emails');
    case ExchangeRuleScopeApplyToType.OUTGOING:
      return translateFn('Outgoing emails');
    case ExchangeRuleScopeApplyToType.ALL:
      return translateFn('All emails');
    default:
      return '';
  }
}
export function getExchangeApplyToRuleFormOptions(): SelectOption[] {
  return [
    {
      title: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.INCOMING),
      value: ExchangeRuleScopeApplyToType.INCOMING,
    },
    {
      title: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.INTERNAL),
      value: ExchangeRuleScopeApplyToType.INTERNAL,
    },
    {
      title: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.OUTGOING),
      value: ExchangeRuleScopeApplyToType.OUTGOING,
    },
    {
      title: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.ALL),
      value: ExchangeRuleScopeApplyToType.ALL,
    },
  ];
}

export function getExchangeAntispamMainAction(value: ExchangeAntispamRuleActionType): string {
  switch (value) {
    case ExchangeAntispamRuleActionType.DELIVER:
      return translateFn('Deliver email');
    case ExchangeAntispamRuleActionType.QUARANTINE:
      return translateFn('Move email to quarantine');
    case ExchangeAntispamRuleActionType.REDIRECT:
      return translateFn('Redirect email');
    case ExchangeAntispamRuleActionType.REJECT:
      return translateFn('Reject email');
    default:
      return '';
  }
}

export function getExchangeAntispamAggressivity(value: ExchangeAntispamAggressivity) {
  switch (value) {
    case ExchangeAntispamAggressivity.PERMISSIVE:
      return translateFn('Permissive');
    case ExchangeAntispamAggressivity.NORMAL:
      return translateFn('Normal');
    case ExchangeAntispamAggressivity.AGGRESSIVE:
      return translateFn('Aggressive');
    default:
      return '';
  }
}
//
export function getExchangeAntispamMainActionGridOptions(): ColumnValue[] {
  return [
    {
      label: getExchangeAntispamMainAction(ExchangeAntispamRuleActionType.DELIVER),
      value: ExchangeAntispamRuleActionType.DELIVER,
    },
    {
      label: getExchangeAntispamMainAction(ExchangeAntispamRuleActionType.QUARANTINE),
      value: ExchangeAntispamRuleActionType.QUARANTINE,
    },
    {
      label: getExchangeAntispamMainAction(ExchangeAntispamRuleActionType.REDIRECT),
      value: ExchangeAntispamRuleActionType.REDIRECT,
    },
    {
      label: getExchangeAntispamMainAction(ExchangeAntispamRuleActionType.REJECT),
      value: ExchangeAntispamRuleActionType.REJECT,
    },
  ];
}

export function getExchangeApplyToRuleGridOptions(): ColumnValue[] {
  return [
    {
      label: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.INCOMING),
      value: ExchangeRuleScopeApplyToType.INCOMING,
    },
    {
      label: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.INTERNAL),
      value: ExchangeRuleScopeApplyToType.INTERNAL,
    },
    {
      label: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.OUTGOING),
      value: ExchangeRuleScopeApplyToType.OUTGOING,
    },
    {
      label: getExchangeApplyToRule(ExchangeRuleScopeApplyToType.ALL),
      value: ExchangeRuleScopeApplyToType.ALL,
    },
  ];
}

export function getExchangeAntispamActiveState(value: ExchangeAntispamActiveState): string {
  switch (value) {
    case ExchangeAntispamActiveState.ACTIVE:
      return translateFn('Enabled');
    case ExchangeAntispamActiveState.INACTIVE:
      return translateFn('Disabled');
    default:
      return '';
  }
}

export function getExchangeAntispamActiveStateGridOptions(): ColumnValue[] {
  return [
    {
      label: getExchangeAntispamActiveState(ExchangeAntispamActiveState.ACTIVE),
      value: ExchangeAntispamActiveState.ACTIVE,
    },
    {
      label: getExchangeAntispamActiveState(ExchangeAntispamActiveState.INACTIVE),
      value: ExchangeAntispamActiveState.INACTIVE,
    },
  ];
}

export function getExchangeRuleSenders(value: ExchangeRuleScopeEmailSelection): string {
  switch (value) {
    case ExchangeRuleScopeEmailSelection.ANY:
      return translateFn('Any');
    case ExchangeRuleScopeEmailSelection.SPECIFIC:
      return translateFn('Specific');
    default:
      return '';
  }
}
export function getExchangeRuleSendersGridOptions(): ColumnValue[] {
  return [
    {
      label: getExchangeRuleSenders(ExchangeRuleScopeEmailSelection.ANY),
      value: ExchangeRuleScopeEmailSelection.ANY,
    },
    {
      label: getExchangeRuleSenders(ExchangeRuleScopeEmailSelection.SPECIFIC),
      value: ExchangeRuleScopeEmailSelection.SPECIFIC,
    },
  ];
}

export function getExchangeAntimalwareScanModeRuleFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('All files'),
      value: ExchangeAntimalwareRuleSettingsScanMode.ALL,
    },
    {
      title: translateFn('Applications only'),
      value: ExchangeAntimalwareRuleSettingsScanMode.ONLY_APPLICATIONS,
    },
    {
      title: translateFn('User defined extensions'),
      value: ExchangeAntimalwareRuleSettingsScanMode.USER_DEFINED_EXTENSIONS,
    },
    {
      title: translateFn('All files, except specific extensions'),
      value: ExchangeAntimalwareRuleSettingsScanMode.ALL_FILES_EXCEPT_SPECIFIC_EXTENSIONS,
    },
  ];
}

export function getExchangeAntimalwareActionRuleFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Remediate'),
      value: ExchangeAntimalwareRuleActionType.REMEDIATE,
    },
    {
      title: translateFn('Reject/Delete email'),
      value: ExchangeAntimalwareRuleActionType.DELETE_EMAIL,
    },
    {
      title: translateFn('Delete file'),
      value: ExchangeAntimalwareRuleActionType.DELETE_ATTACHMENT,
    },
    {
      title: translateFn('Replace file'),
      value: ExchangeAntimalwareRuleActionType.REPLACE_ATTACHMENT,
    },
    {
      title: translateFn('Move file to quarantine'),
      value: ExchangeAntimalwareRuleActionType.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Report only'),
      value: ExchangeAntimalwareRuleActionType.REPORT_ONLY,
    },
  ];
}

export function getExchangeAntimalwareAlternativeActionRuleFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Reject/Delete email'),
      value: ExchangeAntimalwareRuleActionType.DELETE_EMAIL,
    },
    {
      title: translateFn('Delete file'),
      value: ExchangeAntimalwareRuleActionType.DELETE_ATTACHMENT,
    },
    {
      title: translateFn('Replace file'),
      value: ExchangeAntimalwareRuleActionType.REPLACE_ATTACHMENT,
    },
    {
      title: translateFn('Move file to quarantine'),
      value: ExchangeAntimalwareRuleActionType.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Report only'),
      value: ExchangeAntimalwareRuleActionType.REPORT_ONLY,
    },
  ];
}

export function getExchangeUserGroupStatusGridValues(): Array<ColumnValue> {
  return [
    {
      label: translateFn('Enabled'),
      value: ExchangeUserGroupStatus.ENABLED,
    },
    {
      label: translateFn('Disabled'),
      value: ExchangeUserGroupStatus.DISABLED,
    },
  ];
}

export function getExchangeAntimalwareScanTaskTargetType(value: ExchangeAntimalwareScanTaskTargetType): string {
  switch (value) {
    case ExchangeAntimalwareScanTaskTargetType.MAILBOXES_AND_PUBLIC_FOLDERS:
      return translateFn('Mailboxes and Public Folders');
    case ExchangeAntimalwareScanTaskTargetType.MAILBOXES:
      return translateFn('Mailboxes');
    case ExchangeAntimalwareScanTaskTargetType.PUBLIC_FOLDERS:
      return translateFn('Public Folders');
    default:
      return '';
  }
}
export function getExchangeAntimalwareScanTaskTargetTypeFormOptions(): SelectOption[] {
  return [
    {
      title: getExchangeAntimalwareScanTaskTargetType(
        ExchangeAntimalwareScanTaskTargetType.MAILBOXES_AND_PUBLIC_FOLDERS
      ),
      value: ExchangeAntimalwareScanTaskTargetType.MAILBOXES_AND_PUBLIC_FOLDERS,
    },
    {
      title: getExchangeAntimalwareScanTaskTargetType(ExchangeAntimalwareScanTaskTargetType.MAILBOXES),
      value: ExchangeAntimalwareScanTaskTargetType.MAILBOXES,
    },
    {
      title: getExchangeAntimalwareScanTaskTargetType(ExchangeAntimalwareScanTaskTargetType.PUBLIC_FOLDERS),
      value: ExchangeAntimalwareScanTaskTargetType.PUBLIC_FOLDERS,
    },
  ];
}
export function getExchangeAntimalwareScanTaskTargetTypeGridOptions(): ColumnValue[] {
  return [
    {
      label: getExchangeAntimalwareScanTaskTargetType(
        ExchangeAntimalwareScanTaskTargetType.MAILBOXES_AND_PUBLIC_FOLDERS
      ),
      value: ExchangeAntimalwareScanTaskTargetType.MAILBOXES_AND_PUBLIC_FOLDERS,
    },
    {
      label: getExchangeAntimalwareScanTaskTargetType(ExchangeAntimalwareScanTaskTargetType.MAILBOXES),
      value: ExchangeAntimalwareScanTaskTargetType.MAILBOXES,
    },
    {
      label: getExchangeAntimalwareScanTaskTargetType(ExchangeAntimalwareScanTaskTargetType.PUBLIC_FOLDERS),
      value: ExchangeAntimalwareScanTaskTargetType.PUBLIC_FOLDERS,
    },
  ];
}

export function getExchangeAntimalwareScanTaskExclusionType(
  value: ExchangeAntimalwareScanTaskTargetExclusionType
): string {
  switch (value) {
    case ExchangeAntimalwareScanTaskTargetExclusionType.EMAIL:
      return translateFn('Mailbox');
    case ExchangeAntimalwareScanTaskTargetExclusionType.PUBLIC_FOLDER:
      return translateFn('Public folder');
    case ExchangeAntimalwareScanTaskTargetExclusionType.DATABASE:
      return translateFn('Database');
    default:
      return '';
  }
}
export function getExchangeAntimalwareScanTaskExclusionTypeFormOptions(): SelectOption[] {
  return [
    {
      title: getExchangeAntimalwareScanTaskExclusionType(ExchangeAntimalwareScanTaskTargetExclusionType.EMAIL),
      value: ExchangeAntimalwareScanTaskTargetExclusionType.EMAIL,
    },
    {
      title: getExchangeAntimalwareScanTaskExclusionType(ExchangeAntimalwareScanTaskTargetExclusionType.PUBLIC_FOLDER),
      value: ExchangeAntimalwareScanTaskTargetExclusionType.PUBLIC_FOLDER,
    },
    {
      title: getExchangeAntimalwareScanTaskExclusionType(ExchangeAntimalwareScanTaskTargetExclusionType.DATABASE),
      value: ExchangeAntimalwareScanTaskTargetExclusionType.DATABASE,
    },
  ];
}
export function getExchangeAntimalwareScanTaskExclusionTypeGridValues(): ColumnValue[] {
  return [
    {
      label: getExchangeAntimalwareScanTaskExclusionType(ExchangeAntimalwareScanTaskTargetExclusionType.EMAIL),
      value: ExchangeAntimalwareScanTaskTargetExclusionType.EMAIL,
    },
    {
      label: getExchangeAntimalwareScanTaskExclusionType(ExchangeAntimalwareScanTaskTargetExclusionType.PUBLIC_FOLDER),
      value: ExchangeAntimalwareScanTaskTargetExclusionType.PUBLIC_FOLDER,
    },
    {
      label: getExchangeAntimalwareScanTaskExclusionType(ExchangeAntimalwareScanTaskTargetExclusionType.DATABASE),
      value: ExchangeAntimalwareScanTaskTargetExclusionType.DATABASE,
    },
  ];
}

// Status section
export enum ExchangeAntimalwareItemStatus {
  ENABLED = 1,
  DISABLED = 0,
}
export function getExchangeAntimalwareItemStatus(value: ExchangeAntimalwareItemStatus): string {
  switch (value) {
    case ExchangeAntimalwareItemStatus.ENABLED:
      return translateFn('Enabled');
    case ExchangeAntimalwareItemStatus.DISABLED:
      return translateFn('Disabled');
    default:
      return '';
  }
}
export function getExchangeAntimalwareItemStatusGridValues(): Array<ColumnValue> {
  return [
    {
      label: getExchangeAntimalwareItemStatus(ExchangeAntimalwareItemStatus.ENABLED),
      value: ExchangeAntimalwareItemStatus.ENABLED,
    },
    {
      label: getExchangeAntimalwareItemStatus(ExchangeAntimalwareItemStatus.DISABLED),
      value: ExchangeAntimalwareItemStatus.DISABLED,
    },
  ];
}

export function getExchangeContentControlExpressionType(
  value: ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType
): string {
  switch (value) {
    case ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.REGULAR_EXPRESSION:
      return translateFn('Regular expression');
    case ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.TEXT:
      return translateFn('Text');
    default:
      return '';
  }
}
export function getExchangeContentControlExpressionTypeGridOptions(): ColumnValue[] | ListItem[] {
  return [
    {
      label: getExchangeContentControlExpressionType(
        ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.REGULAR_EXPRESSION
      ),
      value: ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.REGULAR_EXPRESSION,
    },
    {
      label: getExchangeContentControlExpressionType(
        ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.TEXT
      ),
      value: ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.TEXT,
    },
  ];
}

export function getExchangeContentControlActionRuleFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Deliver email'),
      value: ExchangeContentControlRuleActionType.DELIVER,
    },
    {
      title: translateFn('Quarantine email'),
      value: ExchangeContentControlRuleActionType.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Redirect email to'),
      value: ExchangeContentControlRuleActionType.REDIRECT,
    },
    {
      title: translateFn('Reject/Delete email'),
      value: ExchangeContentControlRuleActionType.REJECT_EMAIL,
    },
  ];
}

export function getExchangeContentControlActionAttachmentRuleFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Replace file with text'),
      value: ExchangeContentControlRuleActionType.REPLACE_ATTACHMENT,
    },
    {
      title: translateFn('Delete file'),
      value: ExchangeContentControlRuleActionType.DELETE_ATTACHMENT,
    },
    {
      title: translateFn('Reject/Delete email'),
      value: ExchangeContentControlRuleActionType.REJECT_EMAIL,
    },
    {
      title: translateFn('Quarantine email'),
      value: ExchangeContentControlRuleActionType.MOVE_TO_QUARANTINE,
    },
    {
      title: translateFn('Redirect email to'),
      value: ExchangeContentControlRuleActionType.REDIRECT,
    },
    {
      title: translateFn('Deliver email'),
      value: ExchangeContentControlRuleActionType.DELIVER,
    },
  ];
}

export function getExchangeAntispamActionRuleFormOptions(): SelectOption[] {
  return [
    {
      title: translateFn('Deliver email'),
      value: ExchangeAntispamRuleActionType.DELIVER,
    },
    {
      title: translateFn('Move email to quarantine'),
      value: ExchangeAntispamRuleActionType.QUARANTINE,
    },
    {
      title: translateFn('Redirect email to'),
      value: ExchangeAntispamRuleActionType.REDIRECT,
    },
    {
      title: translateFn('Reject/Delete email'),
      value: ExchangeAntispamRuleActionType.REJECT,
    },
  ];
}
